// src/pages/Support.js
import React, { useEffect, useState, useRef } from 'react';
import { db, storage } from '../../firebase';
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
  deleteDoc,
  getDocs,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FaPaperclip } from 'react-icons/fa'; // Attachment icon

function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messagesUnsubscribe, setMessagesUnsubscribe] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null); // For image attachment in chat
  const [modalImageUrl, setModalImageUrl] = useState(null); // For displaying full-size images

  // Ref for file input
  const chatAttachmentInputRef = useRef(null);

  // Fetch and listen for all tickets
  useEffect(() => {
    const ticketsCollectionRef = collection(db, 'tickets');
    const q = query(ticketsCollectionRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ticketsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTickets(ticketsData);
    });

    return () => unsubscribe();
  }, []);

  // Function to select a ticket and load its messages
  const selectTicket = (ticket) => {
    setSelectedTicket(ticket);

    const ticketDocRef = doc(db, 'tickets', ticket.id);
    const messagesCollectionRef = collection(ticketDocRef, 'messages');
    const q = query(messagesCollectionRef, orderBy('timestamp', 'asc'));

    // Unsubscribe from previous messages listener if any
    if (messagesUnsubscribe) {
      messagesUnsubscribe();
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(msgs);
    });

    setMessagesUnsubscribe(() => unsubscribe);
  };

  // Clean up the messages listener when component unmounts
  useEffect(() => {
    return () => {
      if (messagesUnsubscribe) {
        messagesUnsubscribe();
      }
    };
  }, [messagesUnsubscribe]);

  // Send admin message (with optional image)
  const sendMessage = async () => {
    if (newMessage.trim() === '' && !selectedImage) return;
    if (!selectedTicket) return;

    const messageData = {
      sender: 'admin',
      messageText: newMessage,
      timestamp: serverTimestamp(),
      userId: selectedTicket.userId, // For security rules
    };

    try {
      if (selectedImage) {
        // Upload the image to Firebase Storage
        const imageRef = ref(
          storage,
          `tickets/${selectedTicket.id}/messages/${Date.now()}_${selectedImage.name}`
        );
        const snapshot = await uploadBytes(imageRef, selectedImage);
        const downloadURL = await getDownloadURL(snapshot.ref);

        messageData.imageUrl = downloadURL;
        messageData.imageName = selectedImage.name;
      }

      const ticketDocRef = doc(db, 'tickets', selectedTicket.id);
      const messagesCollectionRef = collection(ticketDocRef, 'messages');
      await addDoc(messagesCollectionRef, messageData);

      setNewMessage('');
      setSelectedImage(null);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Attachment icon clicked
  const handleChatAttachmentClick = () => {
    chatAttachmentInputRef.current.click();
  };

  // When user selects an image in the file input
  const handleChatAttachmentChange = (e) => {
    setSelectedImage(e.target.files[0]);
  };

  // Delete entire ticket
  const deleteTicket = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete this ticket? This action cannot be undone.'
      )
    ) {
      try {
        const ticketDocRef = doc(db, 'tickets', selectedTicket.id);

        // Delete attachments associated with the ticket
        const ticketDoc = await getDoc(ticketDocRef);
        const ticketData = ticketDoc.data();

        if (ticketData.attachment && ticketData.attachment.length > 0) {
          const deleteAttachmentPromises = ticketData.attachment.map(
            async (url) => {
              try {
                const fileRef = ref(storage, url);
                await deleteObject(fileRef);
              } catch (error) {
                console.error('Error deleting attachment from storage:', error);
              }
            }
          );
          await Promise.all(deleteAttachmentPromises);
        }

        // Delete all messages in the subcollection and their attachments
        const messagesCollectionRef = collection(ticketDocRef, 'messages');
        const messagesSnapshot = await getDocs(messagesCollectionRef);
        const deleteMessagePromises = messagesSnapshot.docs.map(
          async (messageDoc) => {
            const messageData = messageDoc.data();

            // Delete any attachments in messages
            if (messageData.imageUrl) {
              try {
                const imageRef = ref(storage, messageData.imageUrl);
                await deleteObject(imageRef);
              } catch (error) {
                console.error(
                  'Error deleting message attachment from storage:',
                  error
                );
              }
            }

            // Delete the message document
            await deleteDoc(messageDoc.ref);
          }
        );
        await Promise.all(deleteMessagePromises);

        // Finally, delete the ticket document
        await deleteDoc(ticketDocRef);

        // Close the modal
        setSelectedTicket(null);
      } catch (error) {
        console.error('Error deleting ticket:', error);
        alert('An error occurred while deleting the ticket. Please try again.');
      }
    }
  };

  // Change ticket status
  const changeTicketStatus = async (status) => {
    try {
      const ticketDocRef = doc(db, 'tickets', selectedTicket.id);
      await updateDoc(ticketDocRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  // Open/close image modal
  const openImageModal = (imageUrl) => {
    setModalImageUrl(imageUrl);
  };
  const closeImageModal = () => {
    setModalImageUrl(null);
  };

  // Group tickets by status
  const ticketsByStatus = {
    New: [],
    'In Progress': [],
    Resolved: [],
    Closed: [],
  };

  tickets.forEach((ticket) => {
    const status = ticket.status || 'New';
    if (!ticketsByStatus[status]) {
      ticketsByStatus[status] = [];
    }
    ticketsByStatus[status].push(ticket);
  });

  return (
    <div className="w-full px-4 mt-4">
      {/* Title */}
      <h1 className="mb-4 text-2xl font-bold text-[var(--text-color)]">
        Support Ticket Dashboard
      </h1>

      {/* Grid of tickets by status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {Object.keys(ticketsByStatus).map((status) => (
          <div
            key={status}
            className="
              bg-[var(--card-bg)] p-3 rounded-md shadow-md
              flex flex-col
            "
          >
            <h4 className="text-lg font-semibold text-[var(--text-color)] mb-3">
              {status}
            </h4>
            <ul className="space-y-2">
              {ticketsByStatus[status].map((ticket) => (
                <li
                  key={ticket.id}
                  onClick={() => selectTicket(ticket)}
                  className="
                    p-3 rounded-md cursor-pointer
                    bg-[var(--section-bg)]
                    hover:bg-[var(--card-hover-bg)]
                    transition-all
                  "
                >
                  <strong className="block text-[var(--text-color)]">
                    {ticket.subject}
                  </strong>
                  <span className="text-[var(--muted-color)] text-sm">
                    {ticket.name} - {ticket.priority}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* If a ticket is selected, show modal */}
      {selectedTicket && (
        <div
          className="
            fixed inset-0 z-50 flex items-center justify-center
            bg-black bg-opacity-50
          "
        >
          <div
            className="
              w-full max-w-4xl bg-[var(--card-bg)] text-[var(--text-color)]
              rounded-md shadow-md m-4
              flex flex-col
            "
          >
            {/* Modal Header */}
            <div
              className="
                flex items-center justify-between
                p-4 border-b border-[var(--border-color)]
              "
            >
              <h5 className="text-xl font-semibold">
                Ticket #{selectedTicket.ticketNumber}
              </h5>
              <button
                type="button"
                className="
                  text-sm px-3 py-1
                  rounded-md bg-[var(--primary-color)] 
                  hover:bg-[var(--hover-color)] text-white
                "
                onClick={() => setSelectedTicket(null)}
              >
                Close
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto max-h-[70vh] space-y-4">
              <p>
                <strong>Subject:</strong> {selectedTicket.subject}
              </p>
              <p>
                <strong>Message:</strong> {selectedTicket.message}
              </p>

              {/* Ticket Attachments */}
              {selectedTicket.attachment && selectedTicket.attachment.length > 0 && (
                <div>
                  <h5 className="font-semibold mb-2">Attachments</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedTicket.attachment.map((url, index) => (
                      <div key={index} className="cursor-pointer">
                        <img
                          src={url}
                          alt={`Attachment ${index + 1}`}
                          className="max-w-[100px] rounded-md hover:opacity-80"
                          onClick={() => openImageModal(url)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ticket Info */}
              <p>
                <strong>Name:</strong> {selectedTicket.name}
              </p>
              <p>
                <strong>Email:</strong> {selectedTicket.email}
              </p>
              <p>
                <strong>Priority:</strong> {selectedTicket.priority}
              </p>
              <p>
                <strong>Status:</strong> {selectedTicket.status}
              </p>

              <hr className="border-[var(--border-color)]" />

              {/* Messages */}
              <h5 className="font-semibold">Messages</h5>
              <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`
                      flex flex-col ${
                        msg.sender === 'admin' ? 'items-end' : 'items-start'
                      }
                    `}
                  >
                    <div
                      className="
                        p-2 rounded-md mb-2
                        bg-[var(--input-bg)]
                        text-[var(--text-color)]
                        max-w-[80%]
                        break-words
                      "
                    >
                      {/* Message Image (if any) */}
                      {msg.imageUrl && (
                        <img
                          src={msg.imageUrl}
                          alt={msg.imageName || 'Image'}
                          className="max-w-[100px] mb-1 rounded-md cursor-pointer hover:opacity-80"
                          onClick={() => openImageModal(msg.imageUrl)}
                        />
                      )}
                      {/* Message Text */}
                      {msg.messageText && (
                        <p className="text-sm">{msg.messageText}</p>
                      )}
                      {/* Timestamp */}
                      {msg.timestamp && (
                        <p className="text-xs text-[var(--muted-color)] mt-1">
                          {new Date(
                            msg.timestamp.seconds * 1000
                          ).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Send Message */}
              <div className="flex items-center mt-3 space-x-2">
                <input
                  type="text"
                  className="
                    flex-1 p-2 rounded-md border border-[var(--border-color)]
                    bg-[var(--input-bg)] text-[var(--text-color)]
                    focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                    transition-all
                  "
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                {/* Attachment Icon */}
                <button
                  type="button"
                  onClick={handleChatAttachmentClick}
                  className="
                    p-2 rounded-md bg-[var(--input-bg)]
                    hover:bg-[var(--card-hover-bg)]
                    text-[var(--text-color)]
                  "
                >
                  <FaPaperclip />
                </button>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleChatAttachmentChange}
                  ref={chatAttachmentInputRef}
                  style={{ display: 'none' }}
                />
                <button
                  className="
                    px-4 py-2 rounded-md font-semibold
                    bg-[var(--primary-color)] text-white
                    hover:bg-[var(--hover-color)]
                    transition-all
                  "
                  onClick={sendMessage}
                >
                  Send
                </button>
              </div>
              {/* Show selected image name */}
              {selectedImage && (
                <p className="text-sm text-[var(--muted-color)] mt-2">
                  Selected Image: {selectedImage.name}
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="
                flex items-center justify-end
                p-4 space-x-2 border-t border-[var(--border-color)]
              "
            >
              <select
                className="
                  p-2 rounded-md border border-[var(--border-color)]
                  bg-[var(--input-bg)] text-[var(--text-color)]
                  focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                  transition-all
                "
                value={selectedTicket.status}
                onChange={(e) => changeTicketStatus(e.target.value)}
              >
                <option value="New">New</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>
              <button
                type="button"
                className="
                  px-4 py-2 rounded-md font-semibold
                  bg-red-600 text-white hover:bg-red-700
                  transition-all
                "
                onClick={deleteTicket}
              >
                Delete Ticket
              </button>
              <button
                type="button"
                className="
                  px-4 py-2 rounded-md font-semibold
                  bg-[var(--primary-color)] text-white
                  hover:bg-[var(--hover-color)]
                  transition-all
                "
                onClick={() => setSelectedTicket(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {modalImageUrl && (
        <div
          className="
            fixed inset-0 z-50 flex items-center justify-center
            bg-black bg-opacity-50
          "
          onClick={closeImageModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative p-4"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
          >
            <div
              className="
                max-w-[90vw] max-h-[90vh] 
                bg-[var(--card-bg)] text-[var(--text-color)]
                rounded-md shadow-md p-4
              "
            >
              <div className="flex justify-center items-center">
                <img
                  src={modalImageUrl}
                  alt="Full-size"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex justify-end mt-4">
                <button
                  className="
                    px-4 py-2 rounded-md font-semibold
                    bg-[var(--primary-color)] text-white
                    hover:bg-[var(--hover-color)]
                    transition-all
                  "
                  onClick={closeImageModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSupport;
